import { z } from 'zod';
import { defaultRandom, randomIndex, shuffle } from './random';
import type { RandomSource } from './random';
import { SCHOOL_GROUPS } from './school-group';
import type { SchoolGroup } from './school-group';

export const RACE_GATE_COUNT = 10;
export const RACE_GATE_SECONDS = 9;
export const raceLaneSchema = z.enum(['left', 'right']);
export const raceGateSchema = z.object({
  operator: z.enum(['add', 'subtract', 'multiply', 'divide']),
  operand: z.int().positive().max(1000),
});
export const raceCourseSchema = z.object({
  version: z.literal(1),
  schoolGroup: z.union(SCHOOL_GROUPS.map((group) => z.literal(group))),
  start: z.int(),
  gates: z.array(z.tuple([raceGateSchema, raceGateSchema])).length(RACE_GATE_COUNT),
});
export type RaceLane = z.infer<typeof raceLaneSchema>;
export type RaceGate = z.infer<typeof raceGateSchema>;
export type RaceCourse = z.infer<typeof raceCourseSchema>;
export interface CourseRange {
  readonly worst: number;
  readonly best: number;
}

export function racePreset(group: SchoolGroup) {
  if (group === 3) return { start: 10, operand: 10, factor: 0, min: 0, max: 100 };
  if (group === 4) return { start: 20, operand: 20, factor: 5, min: 0, max: 1000 };
  if (group <= 6) return { start: 50, operand: 100, factor: 10, min: 0, max: 10000 };
  return { start: 100, operand: 1000, factor: 20, min: -100000, max: 100000 };
}

export function applyGate(value: number, gate: RaceGate): number {
  if (!Number.isSafeInteger(value) || !Number.isSafeInteger(gate.operand) || gate.operand <= 0) {
    throw new RangeError('Gates require safe integers and positive operands');
  }
  let result: number;
  switch (gate.operator) {
    case 'add':
      result = value + gate.operand;
      break;
    case 'subtract':
      result = value - gate.operand;
      break;
    case 'multiply':
      result = value * gate.operand;
      break;
    case 'divide':
      result = value / gate.operand;
      break;
  }
  if (!Number.isSafeInteger(result)) throw new RangeError('A gate must produce an exact integer');
  return result;
}

export function inspectCourse(input: RaceCourse): CourseRange {
  const course = raceCourseSchema.parse(input);
  const preset = racePreset(course.schoolGroup);
  if (course.start !== preset.start) throw new RangeError('Invalid starting number');
  let values = [course.start];
  for (const pair of course.gates) {
    for (const gate of pair) {
      const factor = gate.operator === 'multiply' || gate.operator === 'divide';
      if (
        gate.operand > (factor ? preset.factor : preset.operand) ||
        (factor && gate.operand < 2)
      ) {
        throw new RangeError('Gate is outside the school-group preset');
      }
    }
    values = values.flatMap((value) => pair.map((gate) => applyGate(value, gate)));
    if (values.some((value) => value < preset.min || value > preset.max)) {
      throw new RangeError('Course exceeds its score bounds');
    }
  }
  const range = { worst: Math.min(...values), best: Math.max(...values) };
  if (range.best === range.worst) throw new RangeError('The course must have different outcomes');
  return range;
}

export function gradeCourse(value: number, { worst, best }: CourseRange): 1 | 2 | 3 {
  if (
    ![value, worst, best].every(Number.isSafeInteger) ||
    best <= worst ||
    value < worst ||
    value > best
  ) {
    throw new RangeError('Invalid course result');
  }
  const performance = (value - worst) / (best - worst);
  return performance >= 0.8 ? 3 : performance >= 0.5 ? 2 : 1;
}

type Operator = RaceGate['operator'];
type Pair = [RaceGate, RaceGate];
type Kind = readonly [Operator, Operator];
type Preset = ReturnType<typeof racePreset>;

const gate = (operator: Operator, operand: number): RaceGate => ({ operator, operand });

// For positive totals + and × both raise the number, − and : both lower it.
const raises = (operator: Operator) => operator === 'add' || operator === 'multiply';

/**
 * Two gates that move the same way with different operations, such as + 30
 * against × 3. Only working out both answers shows which one is higher.
 */
export function isMixedPair([first, second]: readonly [RaceGate, RaceGate]): boolean {
  return first.operator !== second.operator && raises(first.operator) === raises(second.operator);
}

const MIXED_KINDS: readonly Kind[] = [
  ['add', 'multiply'],
  ['subtract', 'divide'],
];
const PLAIN_KINDS: readonly Kind[] = [
  ['add', 'add'],
  ['subtract', 'subtract'],
  ['multiply', 'multiply'],
  ['divide', 'divide'],
  ['add', 'subtract'],
  ['multiply', 'divide'],
];

function fallbackCourse(schoolGroup: SchoolGroup): RaceCourse {
  const gates: RaceCourse['gates'] =
    schoolGroup === 3
      ? [
          [gate('add', 2), gate('add', 4)],
          [gate('subtract', 1), gate('subtract', 2)],
          [gate('add', 3), gate('add', 5)],
          [gate('subtract', 1), gate('subtract', 3)],
          [gate('add', 2), gate('subtract', 2)],
          [gate('add', 4), gate('add', 6)],
          [gate('subtract', 1), gate('subtract', 2)],
          [gate('add', 1), gate('subtract', 1)],
          [gate('add', 3), gate('add', 4)],
          [gate('subtract', 2), gate('subtract', 4)],
        ]
      : // Valid for the starts and bounds of groups 4, 5–6 and 7–8 alike.
        [
          [gate('subtract', 10), gate('subtract', 20)],
          [gate('add', 20), gate('multiply', 4)],
          [gate('multiply', 2), gate('add', 10)],
          [gate('add', 20), gate('multiply', 2)],
          [gate('multiply', 5), gate('add', 10)],
          [gate('add', 10), gate('add', 20)],
          [gate('subtract', 10), gate('divide', 5)],
          [gate('add', 20), gate('add', 10)],
          [gate('divide', 2), gate('subtract', 10)],
          [gate('add', 20), gate('add', 10)],
        ];
  return { version: 1, schoolGroup, start: racePreset(schoolGroup).start, gates };
}

function gcd(a: number, b: number): number {
  let [x, y] = [Math.abs(a), Math.abs(b)];
  while (y !== 0) [x, y] = [y, x % y];
  return x;
}

function pick<T>(items: readonly T[], random: RandomSource): T | undefined {
  return items.length === 0 ? undefined : items[randomIndex(items.length, random)];
}

interface Reach {
  readonly low: number;
  readonly high: number;
  // Largest number that divides every reachable total exactly.
  readonly divisor: number;
}

// Every gate keeps the order of totals (all operands are positive), so the
// highest reachable total is also where a child on the best route stands.
function samplePair(
  [first, second]: Kind,
  { low, high, divisor }: Reach,
  preset: Preset,
  random: RandomSource,
): Pair | undefined {
  // With division in play, amounts mostly stay multiples of a round step the
  // totals share, so a later : gate can still divide every total exactly.
  const steps = preset.factor > 0 ? [10, 5, 2].filter((step) => divisor % step === 0) : [];
  const step = random() < 0.7 ? (pick(steps, random) ?? 1) : 1;
  const limit = {
    add: Math.min(preset.operand, preset.max - high),
    subtract: Math.min(preset.operand, low - preset.min),
  };
  const factors = Array.from({ length: Math.max(0, preset.factor - 1) }, (_, index) => index + 2);
  const multipliers = factors.filter((f) => high * f <= preset.max && low * f >= preset.min);
  const divisors = factors.filter((f) => divisor % f === 0);
  // Prefer divisors that leave totals divisible by 2 or 5 for the next : gate.
  const keeping = divisors.filter((f) => (divisor / f) % 2 === 0 || (divisor / f) % 5 === 0);

  const amount = (operator: 'add' | 'subtract', target?: number): number | undefined => {
    const unit = Math.floor(limit[operator] / step) >= 1 ? step : 1;
    const most = Math.floor(limit[operator] / unit);
    if (most < 1) return undefined;
    if (target === undefined) return unit * (1 + randomIndex(most, random));
    // Land near the other gate's answer, but never exactly on it.
    let units = Math.min(most, Math.max(1, Math.round((target * (0.7 + 0.6 * random())) / unit)));
    if (units * unit === target) units += units < most ? 1 : -1;
    return units >= 1 ? units * unit : undefined;
  };
  const single = (operator: Operator): RaceGate | undefined => {
    const operand =
      operator === 'add' || operator === 'subtract'
        ? amount(operator)
        : operator === 'multiply'
          ? pick(multipliers, random)
          : pick(keeping.length > 0 && random() < 0.7 ? keeping : divisors, random);
    return operand === undefined ? undefined : gate(operator, operand);
  };

  // Mixed pairs prefer operands whose answers can land close together. Large
  // totals sometimes leave only wider gaps, since + and − amounts are capped.
  if (first === 'add' && second === 'multiply') {
    const close = multipliers.filter((f) => high * (f - 1) * 0.7 <= limit.add);
    const factor = pick(close.length > 0 ? close : multipliers, random);
    const added = factor === undefined ? undefined : amount('add', high * (factor - 1));
    return factor === undefined || added === undefined
      ? undefined
      : [gate('add', added), gate('multiply', factor)];
  }
  if (first === 'subtract' && second === 'divide') {
    const by = pick(keeping.length > 0 && random() < 0.7 ? keeping : divisors, random);
    const taken = by === undefined ? undefined : amount('subtract', high - high / by);
    return by === undefined || taken === undefined
      ? undefined
      : [gate('subtract', taken), gate('divide', by)];
  }
  const one = single(first);
  for (let attempt = 0; one && attempt < 5; attempt += 1) {
    const two = single(second);
    if (two && (two.operator !== one.operator || two.operand !== one.operand)) return [one, two];
  }
  return undefined;
}

function nextPair(
  kinds: readonly Kind[],
  values: readonly number[],
  preset: Preset,
  random: RandomSource,
): { pair: Pair; values: number[] } | undefined {
  const reach = {
    low: Math.min(...values),
    high: Math.max(...values),
    divisor: values.reduce(gcd, 0),
  };
  for (const kind of kinds) {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      const pair = samplePair(kind, reach, preset, random);
      if (!pair) continue;
      const reached = values.flatMap((value) => pair.map((option) => applyGate(value, option)));
      if (reached.some((value) => value < preset.min || value > preset.max)) continue;
      // A pair that gives the same total on every route is not a choice.
      if (values.every((value) => applyGate(value, pair[0]) === applyGate(value, pair[1]))) {
        continue;
      }
      return { pair: random() < 0.5 ? pair : [pair[1], pair[0]], values: [...new Set(reached)] };
    }
  }
  return undefined;
}

// Groups 4 to 8: six of the ten gates are planned as mixed pairs, and a course
// needs at least two + against × and two − against : pairs to be accepted.
function createMixedCourse(schoolGroup: SchoolGroup, random: RandomSource): RaceCourse | undefined {
  const preset = racePreset(schoolGroup);
  const plan = shuffle(
    [
      ...MIXED_KINDS,
      ...MIXED_KINDS,
      ...MIXED_KINDS,
      ...Array.from({ length: RACE_GATE_COUNT - 6 }, () => pick(PLAIN_KINDS, random) as Kind),
    ],
    random,
  );
  const gates: Pair[] = [];
  let values = [preset.start];
  for (const planned of plan) {
    const next = nextPair(
      [planned, ...shuffle([...MIXED_KINDS, ...PLAIN_KINDS], random)],
      values,
      preset,
      random,
    );
    if (!next) return undefined;
    gates.push(next.pair);
    values = next.values;
  }
  const mixed = (operator: Operator) =>
    gates.filter((pair) => isMixedPair(pair) && pair.some((g) => g.operator === operator)).length;
  if (mixed('multiply') < 2 || mixed('divide') < 2) return undefined;
  return { version: 1, schoolGroup, start: preset.start, gates };
}

// Group 3 only adds and subtracts small numbers.
function createBasicCourse(random: RandomSource): RaceCourse | undefined {
  const preset = racePreset(3);
  const gates: Pair[] = [];
  let values = [preset.start];
  for (let index = 0; index < RACE_GATE_COUNT; index += 1) {
    const next = nextPair(
      [[index % 3 === 1 ? 'subtract' : 'add', index % 3 === 0 ? 'add' : 'subtract']],
      values,
      preset,
      random,
    );
    if (!next) return undefined;
    gates.push(next.pair);
    values = next.values;
  }
  return { version: 1, schoolGroup: 3, start: preset.start, gates };
}

// Every candidate is checked against all reachable values, including paths that
// a locally greedy player would never take. Attempts are deliberately bounded.
export function createCourse(
  schoolGroup: SchoolGroup,
  random: RandomSource = defaultRandom,
): RaceCourse {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const course =
      schoolGroup === 3 ? createBasicCourse(random) : createMixedCourse(schoolGroup, random);
    if (course) {
      inspectCourse(course);
      return course;
    }
  }
  const base = fallbackCourse(schoolGroup);
  inspectCourse(base);
  return base;
}

const SYMBOLS = { add: '+', subtract: '−', multiply: '×', divide: ':' } as const;
const WORDS = { add: 'plus', subtract: 'min', multiply: 'keer', divide: 'gedeeld door' } as const;

/** The gate as printed on the arch, with Dutch school notation (: for division). */
export function gateLabel(gate: RaceGate): string {
  return `${SYMBOLS[gate.operator]} ${gate.operand.toLocaleString('nl-NL')}`;
}

/** The gate in words, for screen readers. */
export function gateSpeech(gate: RaceGate): string {
  return `${WORDS[gate.operator]} ${gate.operand.toLocaleString('nl-NL')}`;
}
